import React, { useState, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

import { Button, FabList } from "components";
import { BrandingColorContext } from "components/contexts";
import { FormContent, FormContainer } from "components/layout";
import { WarningMessages } from "globalConstants";
import { getFullName } from "helpers";
import {
	convertFormToSplitProfile,
	defaultSplitInformation,
	StatusValues,
} from "mappers";
import { uiActions } from "store";
import {
	SplitProfileFormProps,
	SplitInformationParamsProps,
	ScenarioTypes,
} from "types";

import { GeneralFields } from "./generalFields";
import { isValidFormValues } from "./isValidFormValues";
import { Override, StraightSplit, Waterfall } from "./scenarios";

import { ReactComponent as SaveIcon } from "static/images/save.svg";

export const SplitProfileForm = ({
	loading,
	isAgentDataLoading,
	splitProfile,
	submit,
	isEditing,
	agent,
}: SplitProfileFormProps) => {
	const dispatch = useDispatch();
	const params = useParams<SplitInformationParamsProps>();

	const [toggleAgentSelect, setToggleAgentSelect] = useState<boolean>(false);

	const methods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		shouldUnregister: false,
		values: splitProfile,
		defaultValues: {
			isHighRisk: "false",
			status: StatusValues[0],
			scenarioType: ScenarioTypes.StraightSplit,
			primarySplitInformation: defaultSplitInformation,
			secondarySplitInformation: [defaultSplitInformation],
			profileName: "",
			totalRevenueShare: "",
		},
	});

	const { watch, handleSubmit, formState, setError, clearErrors } = methods;

	const scenarioType = watch("scenarioType");

	// Scenarios
	const isStraightSplit = useMemo(
		() => scenarioType?.value === ScenarioTypes?.StraightSplit.value,
		[scenarioType?.value],
	);
	const isOverride = useMemo(
		() => scenarioType?.value === ScenarioTypes?.Override.value,
		[scenarioType?.value],
	);
	const isWaterfall = useMemo(
		() => scenarioType?.value === ScenarioTypes?.Waterfall.value,
		[scenarioType?.value],
	);

	const currentAgent = useMemo(() => {
		const agentName = getFullName(agent) || "";

		return {
			id: params.agentId,
			firstName: agent?.firstName,
			lastName: agent?.lastName,
			name: agentName,
			entityType: agent?.isHouseAccountUser ? 1 : 0,
			partnerType: agent?.partnerType,
		};
	}, [agent, params.agentId]);

	const showReassignModal = payload => {
		dispatch(
			uiActions.showModal("REASSIGN_DEFAULT_RESIDUALS_SPLIT_PROFILE", {
				agentId: params.agentId,
				splitProfileId: params.id,
				isOpenPageInNewTab: true,
				warningMessage: WarningMessages.ReassignHighRiskWarningMessage,
				onSuccess: () => submit(payload),
			}),
		);
	};

	const onSubmit = async values => {
		if (!isValidFormValues(values, setError, isOverride)) return;

		let payload = convertFormToSplitProfile(values);

		const isDefaultInitial = splitProfile?.isDefault;

		if (isDefaultInitial && values?.isHighRisk === "true") {
			showReassignModal(payload);
		} else {
			submit(payload);
		}
	};

	return (
		<FormProvider {...methods}>
			<BrandingColorContext.Provider value={true}>
				<form
					data-autotest="splitProfileForm"
					onSubmit={e => {
						e.preventDefault();
						e.stopPropagation();
						clearErrors();
						handleSubmit(onSubmit)();
					}}
				>
					<FormContent>
						<FormContainer>
							<GeneralFields
								isLoading={loading}
								scenarioType={scenarioType}
								splitProfile={splitProfile}
								isEditing={isEditing}
								toggleAgentSelect={toggleAgentSelect}
								isAgentDataLoading={isAgentDataLoading}
							/>

							{isStraightSplit && (
								<StraightSplit
									isLoading={loading}
									splitProfile={splitProfile}
									isEditing={isEditing}
									currentAgent={currentAgent}
								/>
							)}

							{isOverride && (
								<Override
									isLoading={loading}
									setToggleAgentSelect={setToggleAgentSelect}
									toggleAgentSelect={toggleAgentSelect}
									isEditing={isEditing}
									splitProfile={splitProfile}
									currentAgent={currentAgent}
								/>
							)}

							{isWaterfall && (
								<Waterfall
									isLoading={loading}
									setToggleAgentSelect={setToggleAgentSelect}
									toggleAgentSelect={toggleAgentSelect}
									isEditing={isEditing}
									splitProfile={splitProfile}
									currentAgent={currentAgent}
								/>
							)}
						</FormContainer>
					</FormContent>
					<div className="forms-container">
						<FabList>
							<Button
								type="submit"
								disabled={!formState.isDirty}
								value="Save"
								icon={<SaveIcon />}
								size="lg"
								shape="circle"
								isLoading={loading}
							/>
						</FabList>
					</div>
				</form>
			</BrandingColorContext.Provider>
		</FormProvider>
	);
};
